import React from 'react';
import styled from 'styled-components';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from 'primereact/dropdown';
import { useArtists } from "./hooks/useArtists";
import { usePlatforms } from "./hooks/usePlatforms";
import { useLabels } from "./hooks/useLabels";
import { useContributors } from "./hooks/useContributors";
import { IFilters } from '../hooks/useReleases';
import IntlStore from "stories/IntlStore";

interface FiltersProps {
  selectedType: string;
  onTypeChange: (type: string) => void;
  filterArtistIds: string[];
  filterPlatformIds: string[];
  filterLabelIds: string[];
  filterContributorIds: string[];
  onSelectFilter: (filter: IFilters) => void;
  onResetFilters: () => void;
}

export const Filters: React.FC<FiltersProps> = props => {
  const { selectedType, onTypeChange, filterArtistIds, filterContributorIds, filterPlatformIds, filterLabelIds, onSelectFilter, onResetFilters } = props;
  const [loadArtists, setLoadArtists] = React.useState(false);
  const [loadLabels, setLoadLabels] = React.useState(false);
  const [loadContributors, setLoadContributors] = React.useState(false);
  const [loadPlatforms, setLoadPlatforms] = React.useState(false);

  const { artists } = useArtists(loadArtists);
  const { platforms } = usePlatforms(loadPlatforms);
  const { labels } = useLabels(loadLabels);
  const { contributors } = useContributors(loadContributors);
  const isDisabled =
    selectedType === '' &&
    filterArtistIds.length === 0 &&
    filterLabelIds.length === 0 &&
    filterPlatformIds.length === 0 &&
    filterContributorIds.length === 0;
  const typeOptions = [
    { label: IntlStore.intl.formatMessage({ id: "Releases.Filters.TypeAll", defaultMessage: "All" }), value: '' },
    { label: IntlStore.intl.formatMessage({ id: "Releases.Filters.TypeSingle", defaultMessage: "Single" }), value: 'SINGLE' },
    { label: IntlStore.intl.formatMessage({ id: "Releases.Filters.TypeEP", defaultMessage: "EP" }), value: 'EP' },
    { label: IntlStore.intl.formatMessage({ id: "Releases.Filters.TypeAlbum", defaultMessage: "Album" }), value: 'ALBUM' },
  ];
  return (
    <Container>
      <TypeDropdown
        value={selectedType}
        onChange={(e) => onTypeChange(e.value)}
        options={typeOptions}
        placeholder={IntlStore.intl.formatMessage({
          id: "Releases.Filters.TypePlaceholder",
          defaultMessage: "Type"
        })}
      />
      <StyledDropdown
        value={filterArtistIds}
        onChange={(e) => onSelectFilter({ filterArtistIds: e.value })}
        options={artists.map(artist => ({ label: artist.name, value: artist.id }))}
        onShow={() => setLoadArtists(true)}
        placeholder={IntlStore.intl.formatMessage({
          id: "Releases.Filters.ArtistPlaceholder",
          defaultMessage: "Artist"
        })}
        filter
      />
      <StyledDropdown
        value={filterLabelIds}
        onChange={(e) => onSelectFilter({ filterLabelIds: e.value })}
        options={labels.map(label => ({ label: label.name, value: label.id }))}
        onShow={() => setLoadLabels(true)}
        placeholder={IntlStore.intl.formatMessage({
          id: "Releases.Filters.LabelPlaceholder",
          defaultMessage: "Label"
        })}
        filter
      />
      <StyledDropdown
        value={filterContributorIds}
        onChange={(e) => onSelectFilter({ filterContributorIds: e.value })}
        options={contributors.map(contributor => ({ label: contributor.name, value: contributor.id }))}
        onShow={() => setLoadContributors(true)}
        placeholder={IntlStore.intl.formatMessage({
          id: "Releases.Filters.AuthorPlaceholder",
          defaultMessage: "Author"
        })}
        filter
      />
      <StyledDropdown
        value={filterPlatformIds}
        onChange={(e) => onSelectFilter({ filterPlatformIds: e.value })}
        options={platforms.map(platform => ({ label: platform.name, value: platform.id }))}
        onShow={() => setLoadPlatforms(true)}
        placeholder={IntlStore.intl.formatMessage({
          id: "Releases.Filters.DSPPlaceholder",
          defaultMessage: "DSP"
        })}
        filter
      />
      <ResetIconButton
        type="button"
        onClick={onResetFilters}
        aria-disabled={isDisabled}
        $isDisabled={isDisabled}
        aria-label={IntlStore.intl.formatMessage({
          id: "Releases.Filters.Reset",
          defaultMessage: "Reset"
        })}
        title={IntlStore.intl.formatMessage({
          id: "Releases.Filters.Reset",
          defaultMessage: "Reset"
        })}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M19.146 4.854l-1.489 1.489A8 8 0 1 0 12 20a8.094 8.094 0 0 0 7.371-4.886 1 1 0 1 0-1.842-.779A6.071 6.071 0 0 1 12 18a6 6 0 1 1 4.243-10.243l-1.39 1.39a.5.5 0 0 0 .354.854H19.5A.5.5 0 0 0 20 9.5V5.207a.5.5 0 0 0-.854-.353z"
            fill="currentColor"
          />
        </svg>
      </ResetIconButton>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
  width: auto;
  flex: 1 1 auto;
  min-width: 0;
  margin: 0;
  gap: 5px;
  flex-wrap: nowrap;
`;

const StyledDropdown = styled(MultiSelect)`
  width: 135px;
`;

const TypeDropdown = styled(Dropdown)`
  width: 135px;
`;

const ResetIconButton = styled.button<{ $isDisabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  color: rgba(103, 119, 255, 1);
  cursor: pointer;

  cursor: ${p => (p.$isDisabled ? "default" : "pointer")};
  color: ${p => (p.$isDisabled ? "rgba(0, 0, 0, 0.35)" : "rgba(103, 119, 255, 1)")};
  opacity: ${p => (p.$isDisabled ? 0.8 : 1)};

  &:focus {
    outline: none;
    box-shadow: none;
  }
`;
